import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  attendanceColumns,
  businessDayFetchRange,
  computeAttendanceMatrix,
  computePresenceDay,
  computeTimesheet,
  dayPosition,
  rowToTimeEntry,
  timesheetCalendar,
  todayIsoParis,
  type TimeEntry,
  type TimeEntryRow,
  type Timesheet,
} from '@lemontaigu/staff';
import { PresenceGantt } from '@lemontaigu/staff/ui/PresenceGantt.js';
import {
  useRegister,
  useShiftRequestDecisions,
  useTimesheet,
  useTimesheetCorrection,
} from '@lemontaigu/staff/react';
import { useCapability } from '@lemontaigu/platform-ui';
import { CAPABILITIES } from '@lemontaigu/platform';
import {
  activeContractWindow,
  fmtDayLabel,
  fmtHours,
  fmtTime,
  fromDatetimeLocal,
  rowToAdminEmployee,
  shiftSummaryAnchor,
  summaryRange,
  teamLabel,
  toDatetimeLocal,
  TEAMS,
  type AdminEmployee,
  type AdminEmployeeRaw,
  type SummaryGranularity,
} from './logic.js';
import { FrDateInput } from '@lemontaigu/reception/ui/FrDateInput.js';
import { ShiftRequestsInbox } from './ShiftRequestsInbox.js';

// « Pointages » (back-office, R1): the operator payroll tool. is_admin is
// always true here (the whole admin app is is_admin-gated), so the employee
// picker, in-place corrections (traced by the 0018 trigger), manual entries,
// and the printable timeshift report (parent brief §5.3 — the payroll
// deliverable) are always available. The `isOperator` branch is retained
// verbatim from the staff version so the two stay diffable until R2 splits
// them for good.
//
// Column grants (0016): contract dates are not client-readable, so the report
// range is NOT contract-clamped here — for a CDD, select the contract period
// as the range (RUNBOOK §18.8).


export function Pointages() {
  const register = useRegister();
  // `timesheetStore`, not `timesheet` — the latter is the computed report.
  const timesheetStore = useTimesheet();
  const [mode, setMode] = useState<'summary' | 'detail' | 'requests'>('summary');
  const shiftDecisions = useShiftRequestDecisions();
  // The inbox badge: how many employee requests are waiting for a decision.
  const [pendingCount, setPendingCount] = useState(0);
  const refreshPending = useCallback(async () => {
    try {
      setPendingCount((await shiftDecisions.pending()).length);
    } catch {
      // The badge is a hint; the inbox itself surfaces the real error.
    }
  }, [shiftDecisions]);
  useEffect(() => {
    void refreshPending();
  }, [refreshPending]);
  // Ladder step 8: was a `supabase.rpc('is_admin')` fired from the effect below
  // — an authorization question asked over the network, per mount. It is now
  // read from the capability set the composition root fetched once for the
  // whole cockpit (ADR-0013). Same answer, no round trip, and it is the same
  // data RLS enforces with, so this cannot disagree with the database.
  const isOperator = useCapability(CAPABILITIES.PLATFORM_ADMIN);
  const [employees, setEmployees] = useState<AdminEmployee[]>([]);
  const [showPast, setShowPast] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const today = todayIsoParis();
  // Same period model as the overview (operator request): granularity + anchor
  // drive a derived [fromDay, toDay] — no free-form range inputs.
  const [granularity, setGranularity] = useState<SummaryGranularity>('month');
  const [anchor, setAnchor] = useState(today);
  const { fromDay, toDay } = summaryRange(granularity, anchor);
  const [entries, setEntries] = useState<TimeEntry[] | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        setEmployees(await register.listEmployees());
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setErr('');
    if (fromDay === '' || toDay === '' || fromDay > toDay) {
      setEntries([]);
      return;
    }
    // Superset window — computeTimesheet does the exact business-day filtering.
    const window = businessDayFetchRange(fromDay, toDay);
    // The operator picks an employee; a staff session is already scoped by RLS.
    if (isOperator && employeeId === '') {
      setEntries(null);
      return;
    }
    const scope = isOperator ? employeeId : undefined;
    try {
      setEntries(await timesheetStore.entriesBetween(window.fromIso, window.toIso, scope));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [isOperator, employeeId, fromDay, toDay, timesheetStore]);

  useEffect(() => {
    void load();
  }, [load]);

  const timesheet = useMemo(
    () => (entries === null ? null : computeTimesheet(entries, { fromDay, toDay })),
    [entries, fromDay, toDay],
  );

  const selectedEmployee = employees.find((e) => e.id === employeeId) ?? null;

  return (
    <div className="pointages">
      <div className="pointages-modes no-print">
        <button type="button" className={mode === 'summary' ? 'is-active' : ''} onClick={() => setMode('summary')}>
          Vue d’ensemble
        </button>
        <button type="button" className={mode === 'detail' ? 'is-active' : ''} onClick={() => setMode('detail')}>
          Détail par personne
        </button>
        <button type="button" className={mode === 'requests' ? 'is-active' : ''} onClick={() => setMode('requests')}>
          Demandes{pendingCount > 0 ? ` (${pendingCount})` : ''}
        </button>
      </div>

      {mode === 'summary' && (
        <SummaryView
          onOpenPerson={(id, day) => {
            // One click from the overview to that person's detail, on the period
            // being looked at — where adding or fixing a shift happens.
            setEmployeeId(id);
            setGranularity('month');
            setAnchor(day);
            setMode('detail');
          }}
        />
      )}

      {mode === 'requests' && (
        <ShiftRequestsInbox
          employees={employees}
          onChanged={() => {
            void refreshPending();
          }}
        />
      )}

      {mode === 'detail' && (
        <>
      <div className="ts-controls no-print">
        {isOperator && (
          <label className="ts-field">
            <span>Employé·e</span>
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              <option value="">— choisir —</option>
              {employees
                .filter((e) => showPast || e.isCurrent)
                .sort((a, b) => a.firstName.localeCompare(b.firstName, 'fr'))
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.firstName} {e.lastName}
                    {e.nickname ? ` « ${e.nickname} »` : ''}
                    {!e.isCurrent ? ' (ancien·ne)' : ''}
                  </option>
                ))}
            </select>
          </label>
        )}
        {isOperator && (
          <label className="ts-field ts-field-check">
            <input type="checkbox" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} />
            <span>Inclure les ancien·ne·s</span>
          </label>
        )}
        <PeriodNav
          granularity={granularity}
          anchor={anchor}
          onGranularity={setGranularity}
          onAnchor={setAnchor}
        />
        {timesheet !== null && (
          <button type="button" className="auth-btn ts-print" onClick={() => window.print()}>
            Imprimer
          </button>
        )}
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {isOperator && employeeId === '' && (
        <p className="staff-empty">Choisissez un·e employé·e pour afficher ses pointages.</p>
      )}

      {isOperator && employeeId !== '' && <AddEntry employeeId={employeeId} defaultDay={fromDay} onAdded={load} />}

      {timesheet !== null && (
        <TimesheetReport
          timesheet={timesheet}
          employee={isOperator ? selectedEmployee : null}
          employeeId={isOperator ? employeeId : ''}
          isOperator={isOperator}
          onChanged={load}
        />
      )}
        </>
      )}
    </div>
  );
}

const GRANULARITIES: { value: SummaryGranularity; label: string }[] = [
  { value: 'day', label: 'Jour' },
  { value: 'week', label: 'Semaine' },
  { value: 'month', label: 'Mois' },
  { value: 'year', label: 'Année' },
];

/**
 * Period navigator shared by the overview and the per-person detail: a
 * granularity toggle (Jour/Semaine/Mois/Année), a date picker to jump anywhere,
 * ‹ › steppers, and « Aujourd'hui ». Drives a (granularity, anchor) pair; the
 * caller derives [fromDay, toDay] via summaryRange.
 */
export function PeriodNav({
  granularity,
  anchor,
  onGranularity,
  onAnchor,
  fiscalYear = false,
}: {
  granularity: SummaryGranularity;
  anchor: string;
  onGranularity: (g: SummaryGranularity) => void;
  onAnchor: (a: string) => void;
  /** « Année » = the 1 Dec → 30 Nov fiscal year (the books), not the calendar one. */
  fiscalYear?: boolean;
}) {
  const { label } = summaryRange(granularity, anchor, fiscalYear);
  return (
    <div className="pt-periodnav">
      <div className="pt-granularity">
        {GRANULARITIES.map((g) => (
          <button
            key={g.value}
            type="button"
            className={granularity === g.value ? 'is-active' : ''}
            onClick={() => onGranularity(g.value)}
          >
            {g.label}
          </button>
        ))}
      </div>
      <div className="pt-stepper">
        <button type="button" onClick={() => onAnchor(shiftSummaryAnchor(granularity, anchor, -1, fiscalYear))} aria-label="Période précédente">
          ‹
        </button>
        <FrDateInput className="pt-datepicker" value={anchor} onChange={(v) => v && onAnchor(v)} />
        <span className="pt-period-label">{label}</span>
        <button type="button" onClick={() => onAnchor(shiftSummaryAnchor(granularity, anchor, 1, fiscalYear))} aria-label="Période suivante">
          ›
        </button>
        <button type="button" className="pt-today" onClick={() => onAnchor(todayIsoParis())}>
          Aujourd’hui
        </button>
      </div>
    </div>
  );
}

/** TEAMS order, with « sans équipe » last — the row order of the attendance grid. */
const TEAM_SECTIONS: ('' | (typeof TEAMS)[number])[] = [...TEAMS, ''];

/**
 * The class carrying a team's --team-fg (reception's team.css, imported by the
 * admin bundle). Same classes as Équipe, Activités and the board — the colour
 * of a team is decided once, in one stylesheet.
 */
const teamClass = (team: '' | (typeof TEAMS)[number]): string => `team-${team || 'none'}`;

/**
 * All-employee overview (2026-07-18): clocked hours over a Jour / Semaine /
 * Mois period, one compact row each. Shows the THEN-current team — everyone
 * whose contract overlapped the period (not just currently-current), plus
 * anyone with entries in it — so past seasonal staff appear for the months
 * they worked. Drill into a single person via « Détail par personne ».
 *
 * The period's shape decides how it is drawn (operator, 2026-08-06):
 *   Jour   — the three-section presence Gantt (the Pointeuse chart), above the
 *            day's totals. A FUTURE day is drawn as nothing: there is no
 *            attendance to report yet.
 *   others — an attendance grid, one row per person and one column per bucket
 *            (a week's days, a month's weeks, a year's months), grouped by team.
 */
function SummaryView({ onOpenPerson }: { onOpenPerson: (employeeId: string, day: string) => void }) {
  const register = useRegister();
  // `timesheetStore`, not `timesheet` — the latter is the computed report.
  const timesheetStore = useTimesheet();
  const [granularity, setGranularity] = useState<SummaryGranularity>('month');
  const [anchor, setAnchor] = useState(() => todayIsoParis());
  const [employees, setEmployees] = useState<AdminEmployee[]>([]);
  const [entries, setEntries] = useState<TimeEntry[] | null>(null);
  const [err, setErr] = useState('');

  const { fromDay, toDay } = summaryRange(granularity, anchor);
  // « Aujourd'hui » column: fetched separately so it is true whatever period is
  // on screen (a past month still shows who is in today).
  const today = todayIsoParis();
  const [todayEntries, setTodayEntries] = useState<TimeEntry[]>([]);
  useEffect(() => {
    void (async () => {
      const window = businessDayFetchRange(today, today);
      try {
        setTodayEntries(await timesheetStore.entriesBetween(window.fromIso, window.toIso));
      } catch {
        // The main grid reports errors; a missing today column is not worth a second banner.
      }
    })();
  }, [today]);

  useEffect(() => {
    void (async () => {
      try {
        setEmployees(await register.listEmployees());
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  useEffect(() => {
    void (async () => {
      setErr('');
      setEntries(null);
      // Superset window — computeTimesheet does the exact business-day filtering.
      const window = businessDayFetchRange(fromDay, toDay);
      try {
        setEntries(await timesheetStore.entriesBetween(window.fromIso, window.toIso));
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [fromDay, toDay]);

  /**
   * The THEN-current team: under contract during the period, or clocked in it.
   * Carries each person's contract window so the grid can tell « en repos »
   * (under contract, did not work) from « not employed yet » (blank).
   */
  const periodEmployees = useMemo(() => {
    if (entries === null) return [];
    const punched = new Set(entries.map((e) => e.employeeId));
    return employees
      .map((employee) => ({
        employee,
        window: activeContractWindow(employee.contracts, fromDay, toDay),
      }))
      .filter(({ employee, window }) => window !== null || punched.has(employee.id));
  }, [entries, employees, fromDay, toDay]);

  return (
    <section className="pt-summary">
      <div className="pt-summary-controls no-print">
        <PeriodNav
          granularity={granularity}
          anchor={anchor}
          onGranularity={setGranularity}
          onAnchor={setAnchor}
        />
        <button type="button" className="auth-btn ts-print" onClick={() => window.print()}>
          Imprimer
        </button>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {entries === null && !err && <p className="staff-empty">Chargement…</p>}

      {entries !== null && granularity === 'day' && (
        <DayPresence entries={entries} employees={periodEmployees.map((p) => p.employee)} day={fromDay} />
      )}

      {/* Every period, Jour included, is tabulated by the SAME team-grouped
          grid (operator, 2026-08-06) — a day is simply a grid with one column,
          so the reader's eye does not have to re-learn the table per zoom. */}
      {entries !== null && (
        <AttendanceGrid
          entries={entries}
          employees={periodEmployees}
          granularity={granularity}
          fromDay={fromDay}
          toDay={toDay}
          todayEntries={todayEntries}
          today={today}
          onOpenPerson={(id) => onOpenPerson(id, fromDay)}
        />
      )}
    </section>
  );
}

/**
 * The Jour view: the Pointeuse's own three-section chart, over the whole
 * then-current team. What the sections MEAN shifts with the day — today they
 * read as presence, in the past section 1 is « sans départ », a payroll error
 * to correct before the période is closed — and presenceSectionLabels inside
 * the shared chart handles that. A future day gets nothing at all.
 */
function DayPresence({
  entries,
  employees,
  day,
}: {
  entries: TimeEntry[];
  employees: AdminEmployee[];
  day: string;
}) {
  const today = todayIsoParis();
  const presence = useMemo(
    () => computePresenceDay(entries, employees.map((e) => e.id), day, today),
    [entries, employees, day, today],
  );

  if (dayPosition(day, today) === 'future') {
    return <p className="staff-empty">Journée à venir — aucun pointage à afficher.</p>;
  }

  return (
    <div className="pt-day">
      <PresenceGantt day={presence} employees={employees} emptyLabel="Aucun pointage ce jour." />
    </div>
  );
}

/**
 * The tabular half of every period (operator, 2026-08-06): one row per person,
 * one column per bucket, the hours worked in the cell. A day column with no
 * punch reads « Repos »; a bucket before someone was hired (or after they
 * left) stays blank, since that is not a day off.
 *
 * Rows group by team, in TEAMS order, and carry the team's own colour — the
 * same `team-*` classes as Équipe, Activités and the board, so one team is one
 * colour across the whole cockpit. Jour renders this too, with a single
 * column: same table, one zoom in.
 */
function AttendanceGrid({
  entries,
  employees,
  granularity,
  fromDay,
  toDay,
  todayEntries,
  today,
  onOpenPerson,
}: {
  entries: TimeEntry[];
  employees: { employee: AdminEmployee; window: { startDate?: string | null; endDate?: string | null } | null }[];
  granularity: SummaryGranularity;
  fromDay: string;
  toDay: string;
  todayEntries: TimeEntry[];
  today: string;
  onOpenPerson: (employeeId: string) => void;
}) {
  // Team order first, then name — the order the grid rows are grouped in.
  const ordered = useMemo(
    () =>
      [...employees].sort(
        (a, b) =>
          TEAM_SECTIONS.indexOf(a.employee.team) - TEAM_SECTIONS.indexOf(b.employee.team) ||
          `${a.employee.firstName} ${a.employee.lastName}`.localeCompare(
            `${b.employee.firstName} ${b.employee.lastName}`,
            'fr',
          ),
      ),
    [employees],
  );

  const columns = useMemo(
    () => attendanceColumns(granularity, fromDay, toDay),
    [granularity, fromDay, toDay],
  );

  const matrix = useMemo(
    () =>
      computeAttendanceMatrix(
        entries,
        ordered.map(({ employee, window }) => ({ id: employee.id, contract: window ?? undefined })),
        columns,
      ),
    [entries, ordered, columns],
  );

  // The « Aujourd'hui » column: the same matrix machinery over a single day.
  const todayMatrix = useMemo(
    () =>
      computeAttendanceMatrix(
        todayEntries,
        ordered.map(({ employee, window }) => ({ id: employee.id, contract: window ?? undefined })),
        attendanceColumns('day', today, today),
      ),
    [todayEntries, ordered, today],
  );
  const todayById = new Map(todayMatrix.map((r) => [r.employeeId, r.cells[0]!]));
  const todayTotal = todayMatrix.reduce((sum, row) => sum + (row.cells[0]?.hours ?? 0), 0);

  const byId = new Map(ordered.map(({ employee }) => [employee.id, employee]));
  const totals = columns.map((_, i) => matrix.reduce((sum, row) => sum + (row.cells[i]?.hours ?? 0), 0));
  const grandTotal = matrix.reduce((sum, row) => sum + row.totalHours, 0);

  if (ordered.length === 0) {
    return <p className="staff-empty">Personne sous contrat sur la période.</p>;
  }

  // A one-column grid (Jour) would print the same number three times over —
  // the cell, the total and the day count. Only the cell earns its place.
  const showTotals = columns.length > 1;
  let team: '' | (typeof TEAMS)[number] | null = null;

  return (
    <div className="pt-grid-scroll">
      <table className="ts-table pt-grid">
        <thead>
          <tr>
            <th className="pt-grid-name">Employé·e</th>
            <th className="ts-num pt-grid-today" title="Aujourd’hui">
              Aujourd’hui
            </th>
            {columns.map((c) => (
              <th key={c.key} className="ts-num pt-grid-col" title={c.title}>
                {c.label}
              </th>
            ))}
            {showTotals && <th className="ts-num pt-grid-total">Total</th>}
            {showTotals && <th className="ts-num">Jours</th>}
          </tr>
        </thead>
        <tbody>
          {matrix.map((row) => {
            const employee = byId.get(row.employeeId)!;
            const newTeam = team !== employee.team;
            team = employee.team;
            return (
              <Fragment key={row.employeeId}>
                {newTeam && (
                  <tr className={`pt-grid-team ${teamClass(employee.team)}`}>
                    <th colSpan={columns.length + (showTotals ? 4 : 2)}>
                      {employee.team ? teamLabel(employee.team) : 'Sans équipe'}
                    </th>
                  </tr>
                )}
                <tr
                  className={`pt-grid-row ${teamClass(employee.team)}${row.totalHours === 0 ? ' pt-summary-idle' : ''}`}
                  onClick={() => onOpenPerson(employee.id)}
                >
                  <td className="pt-grid-name">
                    <button
                      type="button"
                      className="auth-link pt-grid-person"
                      title="Ouvrir le détail — ajouter ou corriger un pointage"
                    >
                      {employee.firstName} {employee.lastName}
                    </button>
                    {row.openCount > 0 && (
                      <span className="pt-open-flag" title="Pointage sans départ sur la période">
                        {' '}⚠
                      </span>
                    )}
                  </td>
                  <AttendanceCellTd
                    cell={todayById.get(employee.id) ?? { hours: 0, hasPunch: false, openCount: 0, inContract: false }}
                    isDay
                    title={`Aujourd’hui — ${fmtDayLabel(today)}`}
                  />
                  {row.cells.map((cell, i) => (
                    <AttendanceCellTd key={columns[i]!.key} cell={cell} isDay={columns[i]!.isDay} title={columns[i]!.title} />
                  ))}
                  {showTotals && <td className="ts-num pt-grid-total">{fmtHours(row.totalHours)}</td>}
                  {showTotals && <td className="ts-num">{row.daysWorked}</td>}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th className="pt-grid-name">Total équipe</th>
            <th className="ts-num">{todayTotal === 0 ? '' : fmtHoursShort(todayTotal)}</th>
            {totals.map((t, i) => (
              <th key={columns[i]!.key} className="ts-num">
                {t === 0 ? '' : fmtHoursShort(t)}
              </th>
            ))}
            {showTotals && <th className="ts-num pt-grid-total">{fmtHours(grandTotal)}</th>}
            {showTotals && <th className="ts-num" />}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** Compact hours for a grid cell — the unit is in the column header context, not 31 times over. */
function fmtHoursShort(hours: number): string {
  return hours.toLocaleString('fr-FR', { maximumFractionDigits: 1 });
}

function AttendanceCellTd({
  cell,
  isDay,
  title,
}: {
  cell: { hours: number; hasPunch: boolean; openCount: number; inContract: boolean };
  isDay: boolean;
  title: string;
}) {
  // Not employed then — blank. Saying « Repos » would claim a day off that
  // this person was never owed.
  if (!cell.inContract) return <td className="ts-num pt-cell-out" title={`${title} — hors contrat`} />;

  if (cell.openCount > 0) {
    return (
      <td className="ts-num pt-cell-open" title={`${title} — pointage sans départ`}>
        ⚠{cell.hours > 0 ? ` ${fmtHoursShort(cell.hours)}` : ''}
      </td>
    );
  }

  if (!cell.hasPunch) {
    return isDay ? (
      <td className="ts-num pt-cell-off" title={`${title} — repos`}>
        Repos
      </td>
    ) : (
      <td className="ts-num pt-cell-off" title={`${title} — aucun pointage`}>
        —
      </td>
    );
  }

  return (
    <td className="ts-num pt-cell-worked" title={`${title} — ${fmtHours(cell.hours)}`}>
      {fmtHoursShort(cell.hours)}
    </td>
  );
}

function TimesheetReport({
  timesheet,
  employee,
  employeeId,
  isOperator,
  onChanged,
}: {
  timesheet: Timesheet;
  employee: { firstName: string; lastName: string } | null;
  employeeId: string;
  isOperator: boolean;
  onChanged: () => void;
}) {
  // Oldest first — the 1st of the month on top (operator request). The shared
  // helper stays newest-first for the staff app's own relevé.
  const calendarRows = [...timesheetCalendar(timesheet)].reverse();
  return (
    <section className="ts-report">
      <header className="ts-report-head">
        <h2 className="section-title">
          Relevé d’heures{employee ? ` — ${employee.firstName} ${employee.lastName}` : ''}
        </h2>
        <div className="ts-report-range">
          Du {fmtDayLabel(timesheet.fromDay)} au {fmtDayLabel(timesheet.toDay)}
        </div>
      </header>

      <div className="ts-summary">
        <div className="ts-summary-item">
          <span className="ts-summary-value">{fmtHours(timesheet.totalHours)}</span>
          <span className="ts-summary-label">heures travaillées</span>
        </div>
        <div className="ts-summary-item">
          <span className="ts-summary-value">{timesheet.daysWorked}</span>
          <span className="ts-summary-label">jours travaillés</span>
        </div>
        <div className="ts-summary-item">
          <span className="ts-summary-value">{timesheet.daysOff}</span>
          <span className="ts-summary-label">jours de repos</span>
        </div>
      </div>

      {timesheet.openEntryCount > 0 && (
        <p className="ts-warning">
          {timesheet.openEntryCount} pointage{timesheet.openEntryCount > 1 ? 's' : ''} sans départ
          sur la période — à corriger avant l’édition de paie.
        </p>
      )}

      {calendarRows.length === 0 ? (
        <p className="staff-empty">Aucun jour sous contrat sur la période.</p>
      ) : (
        <table className="ts-table">
          <thead>
            <tr>
              <th>Jour</th>
              <th>Arrivée → Départ</th>
              <th className="ts-num">Heures</th>
              <th className="ts-num">Total jour</th>
              {isOperator && <th className="no-print" />}
            </tr>
          </thead>
          <tbody>
            {calendarRows.map((row) =>
              row.worked === null ? (
                <RestDayRow key={row.day} day={row.day} employeeId={employeeId} isOperator={isOperator} onChanged={onChanged} />
              ) : (
                row.worked.shifts.map((shift, i) => (
                  <ShiftRow
                    key={shift.entryId}
                    day={row.day}
                    first={i === 0}
                    rowSpan={row.worked?.shifts.length ?? 1}
                    dayTotal={row.worked?.totalHours ?? 0}
                    shift={shift}
                    isOperator={isOperator}
                    onChanged={onChanged}
                  />
                ))
              ),
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * A day with no clock-in at all. Rendered as a full row (rather than omitted)
 * so a month reads as a calendar and « jours de repos » is auditable line by
 * line — a light red bar carries the meaning without competing with the hours.
 */
function RestDayRow({
  day,
  employeeId,
  isOperator,
  onChanged,
}: {
  day: string;
  employeeId: string;
  isOperator: boolean;
  onChanged: () => void;
}) {
  // A rest day is exactly where « he did work that day » gets said, so the add
  // action lives ON the row, pre-filled with the day (brief §B).
  const [adding, setAdding] = useState(false);
  return (
    <>
      <tr className="ts-off-row">
        <td className="ts-day">{fmtDayLabel(day)}</td>
        <td colSpan={2}>
          <span className="ts-off-bar" aria-hidden="true" />
          <span className="ts-off-label">Repos</span>
        </td>
        <td className="ts-num ts-off-total">—</td>
        {isOperator && (
          <td className="no-print ts-actions">
            {employeeId !== '' && !adding && (
              <button type="button" onClick={() => setAdding(true)}>
                + Ajouter
              </button>
            )}
          </td>
        )}
      </tr>
      {adding && (
        <tr className="no-print">
          <td colSpan={5}>
            <AddEntryForm
              employeeId={employeeId}
              defaultDay={day}
              onDone={() => {
                setAdding(false);
                onChanged();
              }}
              onCancel={() => setAdding(false)}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function ShiftRow({
  day,
  first,
  rowSpan,
  dayTotal,
  shift,
  isOperator,
  onChanged,
}: {
  day: string;
  first: boolean;
  rowSpan: number;
  dayTotal: number;
  shift: { entryId: number; clockIn: string; clockOut: string | null; hours: number | null };
  isOperator: boolean;
  onChanged: () => void;
}) {
  const timesheetCorrection = useTimesheetCorrection();
  const [editing, setEditing] = useState(false);
  const [inValue, setInValue] = useState('');
  const [outValue, setOutValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const startEdit = () => {
    setInValue(toDatetimeLocal(shift.clockIn));
    setOutValue(shift.clockOut ? toDatetimeLocal(shift.clockOut) : '');
    setErr('');
    setEditing(true);
  };

  const save = async () => {
    const clockIn = fromDatetimeLocal(inValue);
    const clockOut = fromDatetimeLocal(outValue);
    if (!clockIn) {
      setErr('Arrivée requise');
      return;
    }
    if (clockOut !== null && clockOut <= clockIn) {
      setErr('Le départ doit suivre l’arrivée');
      return;
    }
    setBusy(true);
    try {
      await timesheetCorrection.correctEntry(shift.entryId, clockIn, clockOut);
    } catch (e) {
      setBusy(false);
      setErr(e instanceof Error ? e.message : String(e));
      return;
    }
    setBusy(false);
    setEditing(false);
    onChanged();
  };

  const remove = async () => {
    if (!window.confirm('Supprimer ce pointage ?')) return;
    setBusy(true);
    try {
      await timesheetCorrection.removeEntry(shift.entryId);
    } catch (e) {
      setBusy(false);
      setErr(e instanceof Error ? e.message : String(e));
      return;
    }
    setBusy(false);
    setEditing(false);
    onChanged();
  };

  return (
    <tr className={shift.hours === null ? 'ts-open' : ''}>
      {first && (
        <td rowSpan={rowSpan} className="ts-day">
          {fmtDayLabel(day)}
        </td>
      )}
      <td>
        {editing ? (
          <span className="ts-edit">
            <input type="datetime-local" value={inValue} onChange={(e) => setInValue(e.target.value)} />
            {' → '}
            <input type="datetime-local" value={outValue} onChange={(e) => setOutValue(e.target.value)} />
            {err && <span className="auth-err"> {err}</span>}
          </span>
        ) : (
          <>
            {fmtTime(shift.clockIn)} →{' '}
            {shift.clockOut ? fmtTime(shift.clockOut) : <em>sans départ — à corriger</em>}
          </>
        )}
      </td>
      <td className="ts-num">{shift.hours === null ? '—' : fmtHours(shift.hours)}</td>
      {first && (
        <td rowSpan={rowSpan} className="ts-num ts-day-total">
          {fmtHours(dayTotal)}
        </td>
      )}
      {isOperator && (
        <td className="no-print ts-actions">
          {editing ? (
            <>
              <button type="button" disabled={busy} onClick={() => void save()}>
                Enregistrer
              </button>
              <button type="button" disabled={busy} className="ts-danger" onClick={() => void remove()}>
                Supprimer
              </button>
              <button type="button" disabled={busy} onClick={() => setEditing(false)}>
                Annuler
              </button>
            </>
          ) : (
            <button type="button" onClick={startEdit}>
              Modifier
            </button>
          )}
        </td>
      )}
    </tr>
  );
}

/**
 * The header-level add control for the person on screen. Defaults to the first
 * day of the period being looked at; the form itself is shared with the
 * per-rest-day « + Ajouter » (AddEntryForm).
 */
function AddEntry({
  employeeId,
  defaultDay,
  onAdded,
}: {
  employeeId: string;
  defaultDay: string;
  onAdded: () => void;
}) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button type="button" className="auth-btn no-print pt-add-shift" onClick={() => setOpen(true)}>
        + Ajouter un pointage
      </button>
    );
  }
  return (
    <AddEntryForm
      employeeId={employeeId}
      defaultDay={defaultDay}
      onDone={() => {
        setOpen(false);
        onAdded();
      }}
      onCancel={() => setOpen(false)}
    />
  );
}

function AddEntryForm({
  employeeId,
  defaultDay,
  onDone,
  onCancel,
}: {
  employeeId: string;
  defaultDay: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const timesheetCorrection = useTimesheetCorrection();
  // Pre-filled with a plain day shift: the usual add is « he worked, the punch
  // was missed », and two edits beat typing two full timestamps.
  const [inValue, setInValue] = useState(`${defaultDay}T09:00`);
  const [outValue, setOutValue] = useState(`${defaultDay}T17:00`);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    const clockIn = fromDatetimeLocal(inValue);
    const clockOut = fromDatetimeLocal(outValue);
    if (!clockIn) {
      setErr('Arrivée requise');
      return;
    }
    if (clockOut !== null && clockOut <= clockIn) {
      setErr('Le départ doit suivre l’arrivée');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await timesheetCorrection.addEntry(employeeId, clockIn, clockOut);
    } catch (e) {
      setBusy(false);
      setErr(e instanceof Error ? e.message : String(e));
      return;
    }
    setBusy(false);
    onDone();
  };

  return (
    <div className="ts-add no-print">
      <label className="ts-field">
        <span>Arrivée</span>
        <input type="datetime-local" value={inValue} onChange={(e) => setInValue(e.target.value)} />
      </label>
      <label className="ts-field">
        <span>Départ (optionnel)</span>
        <input type="datetime-local" value={outValue} onChange={(e) => setOutValue(e.target.value)} />
      </label>
      {err && <p className="auth-err">{err}</p>}
      <div className="cand-actions">
        <button type="button" className="auth-btn" disabled={busy} onClick={() => void save()}>
          Ajouter
        </button>
        <button type="button" className="cand-dismiss" disabled={busy} onClick={onCancel}>
          Annuler
        </button>
      </div>
    </div>
  );
}
